import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import type { AppCategory } from '../types/appRegistry';
import type { CategoryOrders } from '../services/categoryOrderService';

type ConflictState = { visible: boolean; message?: string };
type ServiceModule = {
  getCategoryOrder?: (uid: string) => Promise<any>;
  setCategoryOrder?: (uid: string, payload: any) => Promise<any>;
};

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const copy = arr.slice();
  const val = copy.splice(from, 1)[0];
  copy.splice(to, 0, val);
  return copy;
}

export function useCategoryOrder() {
  const { user } = useAuth();
  const uid = user?.uid || 'anonymous';
  const storageKey = `categoryOrder:${uid}`;

  const [categoryOrders, setCategoryOrders] = useState<CategoryOrders>({});
  const [loading, setLoading] = useState(true);
  const [conflict, setConflict] = useState<ConflictState>({ visible: false });
  const [saving, setSaving] = useState(false);
  const versionRef = useRef(0);
  const serviceRef = useRef<ServiceModule | null>(null);
  const saveTimer = useRef<number | null>(null);

  const initService = useCallback(async () => {
    try {
      const mod = await import('../services/categoryOrderService');
      serviceRef.current = mod;
    } catch {
      serviceRef.current = null;
    }
  }, []);

  const readLocal = useCallback((): { categoryOrders: CategoryOrders; version: number } | null => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const writeLocal = useCallback(
    (data: { categoryOrders: CategoryOrders; version: number }) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {}
    },
    [storageKey]
  );

  const scheduleSave = useCallback(
    (explicitOrders?: CategoryOrders) => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(async () => {
        saveTimer.current = null;
        const orders = explicitOrders ?? { ...categoryOrders };
        const newVersion = (versionRef.current || 0) + 1;
        versionRef.current = newVersion;
        const payload = { categoryOrders: orders, version: newVersion };

        writeLocal(payload);
        setSaving(true);

        const svc = serviceRef.current;
        if (svc?.setCategoryOrder) {
          const res = await svc.setCategoryOrder(uid, payload);
          if (res?.success === false && res.reason === 'conflict') {
            setConflict({ visible: true, message: 'Order changed elsewhere — click to refresh.' });
          }
        }
        setSaving(false);
      }, 700);
    },
    [categoryOrders, uid, writeLocal]
  );

  const loadAuthoritative = useCallback(async () => {
    setLoading(true);
    await initService();
    const svc = serviceRef.current;
    try {
      if (svc?.getCategoryOrder) {
        const res = await svc.getCategoryOrder(uid);
        if (res?.success) {
          versionRef.current = res.version || 0;
          setCategoryOrders(res.categoryOrders || {});
          setConflict({ visible: false });
          setLoading(false);
          return;
        }
      }
    } catch {}

    const local = readLocal();
    if (local) {
      versionRef.current = local.version || 0;
      setCategoryOrders(local.categoryOrders || {});
    }
    setConflict({ visible: false });
    setLoading(false);
  }, [initService, readLocal, uid]);

  useEffect(() => {
    loadAuthoritative();
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [uid]);

  const getOrderForCategory = useCallback(
    (category: AppCategory): string[] => categoryOrders[category] || [],
    [categoryOrders]
  );

  const reorderInCategory = useCallback(
    (category: AppCategory, fromIndex: number, toIndex: number) => {
      setCategoryOrders((prev) => {
        const current = prev[category] || [];
        const next = arrayMove(current, fromIndex, toIndex);
        const updated = { ...prev, [category]: next };
        scheduleSave(updated);
        return updated;
      });
    },
    [scheduleSave]
  );

  const refresh = useCallback(async () => {
    await loadAuthoritative();
  }, [loadAuthoritative]);

  return {
    categoryOrders,
    getOrderForCategory,
    reorderInCategory,
    loading,
    conflict,
    refresh,
    saving,
  };
}
